/**
 * The feedback mode's Escape rung (#13788), and the binding that survives
 * the event which re-enables it (#13911).
 *
 * Extracted from `element-picker.tsx` rather than inlined there: the
 * explanation below is the load-bearing part of this code, and the picker
 * is at the 1000-line cap `check:file-size-ratchet` enforces. A hook is
 * also the honest shape — this is one self-contained keyboard contract.
 *
 * ## The binding has to outlive the event that re-enables it (#13911)
 *
 * This was `useKeyboardShortcut('Escape', deactivate, { enabled: <the
 * guard above> })`, and the guard was — is — correct. What defeated it was
 * the PHASE the two listeners live in. `useKeyboardShortcut` does not
 * consult `enabled` inside its handler; it skips REGISTRATION, so the
 * listener is removed when a thread opens and added back when it closes.
 * Radix binds its dismiss handler at `document` with `{ capture: true }`,
 * so on one keydown: Radix's capture handler closes the popover, React
 * flushes the discrete update before the next listener runs, `pinThreadOpen`
 * goes false, this effect re-adds a bubble listener — and the bubble phase
 * of THAT SAME event then reaches the listener it just added and ends the
 * mode. One press, two rungs, and the pin layer unmounts with the mode
 * because `pinsVisible` carries `isActive` (#13841).
 *
 * So the listeners are registered for the component's whole lifetime —
 * `src/app/layout.tsx` mounts this on every route, mode or no mode — and
 * the guard moves inside. `useKeyboardShortcut`'s register-on-`enabled`
 * semantics are relied on by many other callers and are deliberately not
 * changed repo-wide; `deactivate` is the one caller that needs this.
 *
 * ## Reading the guard from a ref is NOT sufficient by itself
 *
 * The flush above happens mid-event, so a ref written during render is
 * already `false` by the time the bubble phase runs — it answers for the
 * wrong instant. The decision therefore has to be taken when the event
 * STARTS, which is what the capture-phase listener below is for: it
 * records the guard for this event object, and the bubble-phase listener
 * acts on that recording rather than on whatever the tree believes a
 * commit later. It is registered on mount, before any popover in the page
 * exists, so it runs ahead of every Radix layer's capture handler.
 *
 * `defaultPrevented` is the second, independent belt, and it covers every
 * layer that dismisses in the CAPTURE phase: Radix's `DismissableLayer`
 * calls `preventDefault()` before it dismisses, so a key already spent by a
 * popover, a dropdown, or a future Radix layer nobody has written yet is
 * visibly spent by the time this listener sees it, with no enumeration of
 * layer types here.
 *
 * ## What `defaultPrevented` does NOT cover, and the third guard
 *
 * It covers a layer above only when that layer runs BEFORE this one, and
 * `useModalEscape` does not: it installs a single BUBBLE-phase `document`
 * listener LAZILY, on the first modal push (`use-modal-escape.ts`), which
 * is necessarily after this component's mount-time registration. Bubble
 * listeners fire in registration order, so `exitMode` runs FIRST and sees
 * `defaultPrevented === false` even though the modal above is about to
 * claim the key. A general app modal does not set `anchor` or
 * `plainFeedbackRequested` either — those two cover the FEEDBACK modal —
 * so the guard would otherwise read `true` and end the mode underneath an
 * unrelated dialog. `browsing` makes that reachable: it is a live mode with
 * no capture layer, so ordinary dialogs open over it.
 *
 * `useModalEscape` anticipates exactly this and publishes the read to use:
 * `isModalEscapeStackActive()`. READ the stack, never push onto it —
 * pushing would make Escape close the mode INSTEAD of the dialog, which is
 * the opposite failure.
 *
 * ## …except when the picker is drawn OVER the dialog (#16841)
 *
 * The yield above is right whenever the dialog is the topmost thing on
 * screen — `browsing`, or an anchor pending. Since #16841 it is not always:
 * the picker's capture layer is `z-feedback-picker`, above every ordinary
 * modal's `z-modal` (#16990 names; they were 540 and 500), so a viewer who presses `Shift+F` from inside a dialog sees the
 * picker and the banner ON TOP of it. Yielding there closed the dialog
 * underneath the tool the viewer was actually using, which is the reverse of
 * what is on screen. So while the picker is up (`isPicking`, the picker's
 * own `visible`) AND a modal holds the stack, the FIRST Escape leaves the
 * mode and the dialog stays open; the next one is the dialog's again.
 *
 * MECHANISM — chosen because it needs one special case where the
 * alternative needs several: feedback mode TAKES the key in the CAPTURE
 * phase, before the stack, rather than pushing onto `useModalEscape`. A push
 * would have to be added and withdrawn in lockstep with `visible`, would
 * then sit ABOVE a `FeedbackModal` opened later only if pop order happened to
 * match, and would make the stack's "topmost closes" rule lie about a
 * participant that is not a dialog. Taking the key instead is one branch in
 * the capture listener below: `preventDefault()` + `stopImmediatePropagation`
 * spend the press, so neither the stack's bubble listener nor a later
 * `document` listener ever sees it — and since the condition requires
 * `isPicking`, which is false whenever an anchor is pending or a pin thread
 * is open, the #13788 ladder inside `FeedbackModal` and the #13911 thread
 * rung are untouched. With no modal open the branch does not fire and the
 * bubble path below handles the key exactly as before.
 *
 * The branch assumes every modal on the stack is BELOW the picker. That holds
 * because a dialog opening AFTER picking began suspends the picker to
 * `browsing` (`use-picker-yields-to-new-modal.ts`), so `isPicking` is false
 * by the time the new dialog could take a key — including a `FeedbackModal`
 * opened from outside the mode, which sits above the picker on `z-feedback-modal`.
 */

import { useEffect, useLayoutEffect, useRef } from 'react'
import { isModalEscapeStackActive } from '@/hooks/use-modal-escape'

interface FeedbackModeEscapeOptions {
  /**
   * The provider's `enabled` (true under the root layout since #16723). The
   * ONLY input that may gate registration — see the effect below.
   */
  isEnabled: boolean
  /** The assembled ladder guard: may this Escape end the mode? */
  canExitOnEscape: boolean
  /**
   * Is the picker's capture layer up (#16841)? When it is, the picker is
   * drawn above any open ordinary modal, so Escape leaves the mode before
   * the modal stack gets the key. Defaults to `false`: yield as before.
   */
  isPicking?: boolean
  /** What to do when it may. */
  deactivate: () => void
}

export function useFeedbackModeEscape({
  isEnabled,
  canExitOnEscape,
  isPicking = false,
  deactivate,
}: FeedbackModeEscapeOptions): void {
  const canExitOnEscapeRef = useRef(canExitOnEscape)
  const isPickingRef = useRef(isPicking)
  const deactivateRef = useRef(deactivate)

  // Written from effects, not during render. A render-phase `ref.current = x`
  // publishes a value React has not committed — and a render that is thrown
  // away publishes it anyway — which is precisely the "the answer changed
  // under the event" failure this whole binding exists to stop.
  //
  // LAYOUT effects, not passive ones, and the difference is load-bearing
  // here in a way it is not for `use-keyboard-shortcut.ts`'s equivalent. A
  // passive effect is flushed on a scheduler task; React does flush pending
  // passive effects before dispatching a discrete event, but only for events
  // arriving through ITS OWN delegated listener at the root container. The
  // two listeners below are native `document` listeners, and `document`
  // capture runs before the root container sees anything — so an Escape
  // pressed in the window between "thread opened, commit done" and "passive
  // effects flushed" would snapshot the PREVIOUS guard and end the mode. That
  // is the original defect arriving by a second route. A layout effect runs
  // synchronously inside the commit, so the ref is current before the browser
  // can deliver another event at all.
  useLayoutEffect(() => {
    canExitOnEscapeRef.current = canExitOnEscape
  }, [canExitOnEscape])

  useLayoutEffect(() => {
    isPickingRef.current = isPicking
  }, [isPicking])

  useLayoutEffect(() => {
    deactivateRef.current = deactivate
  }, [deactivate])

  useEffect(() => {
    // The provider's `enabled`, and ONLY that, may gate registration. It is
    // fixed by the root layout (`<FeedbackModeProvider enabled>`), so it is
    // already final on this component's first client render and cannot flip
    // mid-event the way `pinThreadOpen` does. That is the whole distinction:
    // a disabled provider should not carry two permanent `document`
    // listeners, but nothing narrower than `enabled` is safe to register on,
    // or the defect returns.
    if (!isEnabled) return

    // Plain Escape only, matching `useKeyboardShortcut`'s exact-modifier
    // contract: Shift+Escape and Cmd+Escape are somebody else's key.
    //
    // `event.repeat` is excluded for the reason this whole file exists: a
    // HELD Escape is ONE physical press, and auto-repeat turns it into a
    // stream of `keydown`s. The first closes the popover; without this term
    // the second ends the mode — one press, two rungs, which is the defect
    // restated in the time domain rather than the phase domain. Nothing is
    // lost by ignoring repeats: the first keydown of any press is not a
    // repeat, so every deliberate press is still served.
    const isPlainEscape = (event: KeyboardEvent) =>
      event.key === 'Escape' &&
      !event.repeat &&
      !event.shiftKey &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey

    // Both guards read at one instant. The modal-stack read belongs HERE
    // rather than in `exitMode` for the same reason the ladder guard does:
    // the stack is what it is when the key is pressed, and a listener that
    // pops it runs later in the same dispatch.
    const canExitNow = () =>
      canExitOnEscapeRef.current && !isModalEscapeStackActive()

    // The guard, as of the start of the event carrying it. Keyed by the event
    // OBJECT so a dispatch that never reaches the bubble phase cannot leave a
    // decision behind for the next one to read.
    let decision: { event: KeyboardEvent; canExit: boolean } | null = null

    // #16841 — the picker is drawn over an open dialog, so the mode is the
    // topmost thing on screen and takes this press before the modal stack.
    const pickerCoversModal = () =>
      canExitOnEscapeRef.current &&
      isPickingRef.current &&
      isModalEscapeStackActive()

    const recordDecision = (event: KeyboardEvent) => {
      if (!isPlainEscape(event)) return
      if (!event.defaultPrevented && pickerCoversModal()) {
        decision = null
        event.preventDefault()
        event.stopImmediatePropagation()
        deactivateRef.current()
        return
      }
      decision = { event, canExit: canExitNow() }
    }

    const exitMode = (event: KeyboardEvent) => {
      if (!isPlainEscape(event)) return
      const canExit =
        decision?.event === event ? decision.canExit : canExitNow()
      decision = null
      // A layer above already spent this press closing itself.
      if (event.defaultPrevented) return
      if (!canExit) return
      event.preventDefault()
      deactivateRef.current()
    }

    document.addEventListener('keydown', recordDecision, { capture: true })
    document.addEventListener('keydown', exitMode)
    return () => {
      document.removeEventListener('keydown', recordDecision, { capture: true })
      document.removeEventListener('keydown', exitMode)
    }
  }, [isEnabled])
}
